import React, { useState, useEffect } from 'react';
import { Button } from '@components/common/button';
import { Input } from '@components/common/input';
import { Modal } from '@components/common/modal';
import {
  Calendar,
  Plus,
  Trash2,
  Percent,
  Loader,
} from 'lucide-react';
import { CreateInvoicePayload, Invoice, InvoiceLineItem, InvoiceStatus } from '@/types/invoice_types';
import { UnbilledReadingForInvoicing } from '@/types/meter_types';
import { Client } from '@/types/client_types';
import { fetch_unbilled_readings_for_invoicing } from '@/services/meter_service';

type UnbilledReading = UnbilledReadingForInvoicing;

interface InvoiceFormProps {
  is_open: boolean;
  on_close: () => void;
  on_submit: (data: CreateInvoicePayload) => void;
  initial_data?: Invoice;
  clients: Client[];
  is_loading?: boolean;
  mode?: 'create' | 'edit';
}

interface FormData {
  client: string;
  meter_id: string;
  billing_period_start: string;
  billing_period_end: string;
  issue_date: string;
  due_date: string;
  line_items: InvoiceLineItem[];
  tax_rate: number;
  notes: string;
  terms: string;
  status: InvoiceStatus;
}

export const InvoiceForm: React.FC<InvoiceFormProps> = ({
  is_open,
  on_close,
  on_submit,
  initial_data,
  clients,
  is_loading = false,
  mode = 'create'
}) => {
  const [selected_reading_id, set_selected_reading_id] = useState<string>('');
  const [unbilled_readings, set_unbilled_readings] = useState<UnbilledReading[]>([]);
  const [loading_readings, set_loading_readings] = useState(false);
  
  const [form_data, set_form_data] = useState<FormData>({
    client: '',
    meter_id: '',
    billing_period_start: '',
    billing_period_end: '',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: '',
    line_items: [
      {
        description: 'Water Consumption',
        quantity: 0,
        unit_price: 0,
        amount: 0
      }
    ],
    tax_rate: 16,
    notes: '',
    terms: 'Payment is due within 30 days',
    status: 'draft'
  });

  const [errors, set_errors] = useState<Record<string, string>>({});
  const [selected_client, set_selected_client] = useState<Client | null>(null);
  const [auto_populated, set_auto_populated] = useState(false);

  // Load unbilled readings when modal opens in create mode
  useEffect(() => {
    if (is_open && mode === 'create') {
      load_unbilled_readings();
    }
  }, [is_open, mode]);

  const load_unbilled_readings = async () => {
    set_loading_readings(true);
    try {
      console.log('Fetching unbilled readings...');
      const readings = await fetch_unbilled_readings_for_invoicing();
      console.log('Unbilled readings loaded:', readings);
      set_unbilled_readings(readings);
    } catch (error) {
      console.error('Error loading unbilled readings:', error);
      set_unbilled_readings([]);
    } finally {
      set_loading_readings(false);
    }
  };

  // Populate form when editing or reset when creating
  useEffect(() => {
    if (initial_data && mode === 'edit') {
      const issue_date_str = typeof initial_data.issue_date === 'string'
        ? initial_data.issue_date
        : new Date(initial_data.issue_date).toISOString().split('T')[0];

      set_form_data({
        client: initial_data.client.toString(),
        meter_id: initial_data.meter_id?.toString() || '',
        billing_period_start: initial_data.billing_period_start,
        billing_period_end: initial_data.billing_period_end,
        issue_date: issue_date_str,
        due_date: initial_data.due_date,
        line_items: initial_data.items?.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          amount: item.total // Use item.total as the amount
        })) || [],
        tax_rate: initial_data.tax_rate || 16,
        notes: initial_data.notes || '',
        terms: initial_data.terms || 'Payment is due within 30 days',
        status: initial_data.status
      });

      const client = clients.find(c => c.id === initial_data.client);
      set_selected_client(client || null);
    } else if (!initial_data) {
      set_form_data({
        client: '',
        meter_id: '',
        billing_period_start: '',
        billing_period_end: '',
        issue_date: new Date().toISOString().split('T')[0],
        due_date: '',
        line_items: [
          {
            description: 'Water Consumption',
            quantity: 0,
            unit_price: 0,
            amount: 0
          }
        ],
        tax_rate: 16,
        notes: '',
        terms: 'Payment is due within 30 days',
        status: 'draft'
      });
      set_selected_client(null);
      set_selected_reading_id('');
      set_auto_populated(false);
    }
    set_errors({});
  }, [initial_data, mode, is_open, clients]);

  // Auto-populate from selected unbilled reading
  useEffect(() => {
    if (selected_reading_id && mode === 'create') {
      const reading = unbilled_readings.find(r => r.reading_id.toString() === selected_reading_id);
      if (reading) {
        populate_from_reading(reading);
      }
    }
  }, [selected_reading_id]);

  const populate_from_reading = (reading: UnbilledReading) => {
    const due_date = new Date();
    due_date.setDate(due_date.getDate() + 30);

    const suggested = reading.suggested_invoice_data;

    set_form_data({
      client: suggested.client_id.toString(),
      meter_id: suggested.meter_id.toString(),
      billing_period_start: suggested.billing_period_start,
      billing_period_end: suggested.billing_period_end,
      issue_date: new Date().toISOString().split('T')[0],
      due_date: due_date.toISOString().split('T')[0],
      line_items: suggested.line_items.map((item: any) => ({
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        amount: item.amount
      })),
      tax_rate: suggested.tax_rate,
      notes: suggested.notes,
      terms: 'Payment is due within 30 days',
      status: 'draft'
    });

    const client = clients.find(c => c.id === reading.meter.client_id);
    set_selected_client(client || null);
    set_auto_populated(true);
  };

  const calculate_line_item_amount = (quantity: number, unit_price: number): number => {
    return quantity * unit_price;
  };

  const calculate_subtotal = (): number => {
    return form_data.line_items.reduce((sum, item) => sum + item.amount, 0);
  };

  const calculate_tax_amount = (): number => {
    return (calculate_subtotal() * form_data.tax_rate) / 100;
  };

  const calculate_total = (): number => {
    return calculate_subtotal() + calculate_tax_amount();
  };

  const validate_form = (): boolean => {
    const new_errors: Record<string, string> = {};

    if (!form_data.client) {
      new_errors.client = 'Client is required';
    }

    if (!form_data.billing_period_start) {
      new_errors.billing_period_start = 'Billing period start date is required';
    }

    if (!form_data.billing_period_end) {
      new_errors.billing_period_end = 'Billing period end date is required';
    }

    if (form_data.billing_period_start && form_data.billing_period_end) {
      if (new Date(form_data.billing_period_end) <= new Date(form_data.billing_period_start)) {
        new_errors.billing_period_end = 'End date must be after start date';
      }
    }

    if (!form_data.issue_date) {
      new_errors.issue_date = 'Issue date is required';
    }

    if (!form_data.due_date) {
      new_errors.due_date = 'Due date is required';
    }

    if (form_data.issue_date && form_data.due_date) {
      if (new Date(form_data.due_date) < new Date(form_data.issue_date)) {
        new_errors.due_date = 'Due date cannot be before issue date';
      }
    }

    if (form_data.line_items.length === 0) {
      new_errors.line_items = 'At least one line item is required';
    } else {
      form_data.line_items.forEach((item, index) => {
        if (!item.description.trim()) {
          new_errors[`line_item_${index}_description`] = 'Description is required';
        }
        if (item.quantity <= 0) {
          new_errors[`line_item_${index}_quantity`] = 'Quantity must be greater than 0';
        }
        if (item.unit_price <= 0) {
          new_errors[`line_item_${index}_unit_price`] = 'Unit price must be greater than 0';
        }
      });
    }

    if (form_data.tax_rate < 0 || form_data.tax_rate > 100) {
      new_errors.tax_rate = 'Tax rate must be between 0 and 100';
    }

    set_errors(new_errors);
    return Object.keys(new_errors).length === 0;
  };

  const handle_submit = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    if (validate_form()) {
      const submission_data: CreateInvoicePayload = {
        client: form_data.client,
        meter_id: form_data.meter_id || undefined,
        billing_period_start: form_data.billing_period_start,
        billing_period_end: form_data.billing_period_end,
        issue_date: form_data.issue_date,
        due_date: form_data.due_date,
        line_items: form_data.line_items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          amount: item.amount
        })),
        tax_rate: form_data.tax_rate,
        notes: form_data.notes,
        terms: form_data.terms,
        status: form_data.status, // Now properly typed as InvoiceStatus
        subtotal: calculate_subtotal(),
        tax_amount: calculate_tax_amount(),
        total_amount: calculate_total()
      };

      console.log('Submitting invoice with items:', {
        items_count: submission_data.line_items.length,
        items: submission_data.line_items,
        total_amount: submission_data.total_amount
      });
      
      on_submit(submission_data);
    }
  };

  const handle_change = (field: keyof FormData, value: any) => {
    set_form_data(prev => ({ ...prev, [field]: value }));

    if (errors[field]) {
      set_errors(prev => {
        const new_errors = { ...prev };
        delete new_errors[field];
        return new_errors;
      });
    }
  };

  const handle_client_change = (client_id: string) => {
    handle_change('client', client_id);
    const client = clients.find(c => c.id.toString() === client_id);
    set_selected_client(client || null);
  };

  const handle_line_item_change = (
    index: number,
    field: keyof Omit<InvoiceLineItem, 'id'>,
    value: any
  ) => {
    const updated_items = [...form_data.line_items];
    updated_items[index] = {
      ...updated_items[index],
      [field]: value
    };

    // Don't allow quantity change for first item if auto-populated from reading
    if (field === 'quantity' && auto_populated && index === 0) {
      return;
    }

    // Recalculate amount when quantity or unit_price changes
    if (field === 'quantity' || field === 'unit_price') {
      updated_items[index].amount = calculate_line_item_amount(
        updated_items[index].quantity,
        updated_items[index].unit_price
      );
    }

    set_form_data(prev => ({ ...prev, line_items: updated_items }));

    const error_key = `line_item_${index}_${field}`;
    if (errors[error_key]) {
      set_errors(prev => {
        const new_errors = { ...prev };
        delete new_errors[error_key];
        return new_errors;
      });
    }
  };

  const add_line_item = () => {
    set_form_data(prev => ({
      ...prev,
      line_items: [
        ...prev.line_items,
        {
          description: '',
          quantity: 1,
          unit_price: 0,
          amount: 0
        }
      ]
    }));
  };

  const remove_line_item = (index: number) => {
    if (form_data.line_items.length > 1) {
      set_form_data(prev => ({
        ...prev,
        line_items: prev.line_items.filter((_, i) => i !== index)
      }));
    }
  };

  const handle_close = () => {
    if (!is_loading) {
      on_close();
    }
  };

  const handle_save_as_draft = () => {
    set_form_data(prev => ({ ...prev, status: 'draft' }));
    setTimeout(() => {
      handle_submit();
    }, 0);
  };

  const format_currency = (amount: number) => {
    return `KES ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const subtotal = calculate_subtotal();
  const tax_amount = calculate_tax_amount();
  const total = calculate_total();

  return (
    <Modal
      is_open={is_open}
      on_close={handle_close}
      title={mode === 'create' ? 'Create New Invoice' : 'Edit Invoice'}
      size="xl"
      footer={
        <div className="flex gap-3 justify-end">
          <Button
            variant="secondary"
            onClick={handle_close}
            disabled={is_loading}
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handle_save_as_draft}
            disabled={is_loading}
          >
            Save as Draft
          </Button>
          <Button
            variant="primary"
            onClick={() => handle_submit()}
            is_loading={is_loading}
          >
            {mode === 'create' ? 'Create & Send' : 'Update Invoice'}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Quick Create from Reading */}
        {mode === 'create' && (
          <div className="p-4 bg-primary-10 border border-forest-green rounded-lg">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <p className="text-sm font-semibold text-forest-green mb-2">
                  Quick Create from Meter Reading
                </p>
                
                {loading_readings ? (
                  <div className="flex items-center gap-2 text-sm text-medium-gray">
                    <Loader size={16} className="animate-spin" />
                    Loading unbilled readings...
                  </div>
                ) : unbilled_readings.length > 0 ? (
                  <>
                    <select
                      value={selected_reading_id}
                      onChange={(e) => set_selected_reading_id(e.target.value)}
                      className="w-full px-4 py-2 border border-forest-green rounded-lg focus:outline-none focus:ring-2 focus:ring-forest-green focus:border-forest-green transition-all text-charcoal"
                    >
                      <option value="">Select a meter reading to auto-populate...</option>
                      {unbilled_readings.map(reading => (
                        <option key={reading.reading_id} value={reading.reading_id}>
                          {reading.meter.meter_number} - {reading.meter.client_name} - 
                          {reading.consumption.consumption.toFixed(2)} m³ ({reading.billing_period.label})
                        </option>
                      ))}
                    </select>
                    <p className="mt-1.5 text-xs text-medium-gray">
                      This will automatically fill in the invoice details. You can still make adjustments.
                    </p>
                  </>
                ) : (
                  <div className="text-sm text-medium-gray">
                    No unbilled meter readings found. Please record meter readings first.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Client Selection */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-charcoal uppercase tracking-wide">
            Client Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-charcoal mb-1.5">
                Select Client <span className="text-error">*</span>
              </label>
              <select
                value={form_data.client}
                onChange={(e) => handle_client_change(e.target.value)}
                disabled={auto_populated}
                className="w-full px-4 py-2 border border-light-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-forest-green focus:border-forest-green transition-all disabled:bg-muted disabled:cursor-not-allowed text-charcoal"
              >
                <option value="">Select a client...</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.name} - {client.client_type}
                  </option>
                ))}
              </select>
              {errors.client && (
                <p className="mt-1.5 text-sm text-error">{errors.client}</p>
              )}
            </div>

            {selected_client && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Client Details</p>
                <p className="text-sm font-medium text-charcoal">{selected_client.name}</p>
                {selected_client.email && (
                  <p className="text-xs text-medium-gray">{selected_client.email}</p>
                )}
                {selected_client.phone && (
                  <p className="text-xs text-medium-gray">{selected_client.phone}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Billing Period & Dates */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-charcoal uppercase tracking-wide">
            Billing Period & Dates
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Billing Period Start"
              type="date"
              value={form_data.billing_period_start}
              onChange={(e) => handle_change('billing_period_start', e.target.value)}
              icon_left={<Calendar size={18} />}
              error={errors.billing_period_start}
              is_required
            />

            <Input
              label="Billing Period End"
              type="date"
              value={form_data.billing_period_end}
              onChange={(e) => handle_change('billing_period_end', e.target.value)}
              icon_left={<Calendar size={18} />}
              error={errors.billing_period_end}
              is_required
            />

            <Input
              label="Issue Date"
              type="date"
              value={form_data.issue_date}
              onChange={(e) => handle_change('issue_date', e.target.value)}
              icon_left={<Calendar size={18} />}
              error={errors.issue_date}
              is_required
            />

            <Input
              label="Due Date"
              type="date"
              value={form_data.due_date}
              onChange={(e) => handle_change('due_date', e.target.value)}
              icon_left={<Calendar size={18} />}
              error={errors.due_date}
              is_required
            />
          </div>
        </div>

        {/* Line Items */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-charcoal uppercase tracking-wide">
              Line Items
            </h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={add_line_item}
              icon_left={<Plus size={16} />}
            >
              Add Item
            </Button>
          </div>

          {errors.line_items && (
            <p className="text-sm text-error">{errors.line_items}</p>
          )}

          <div className="space-y-3">
            {form_data.line_items.map((item, index) => (
              <div key={index} className="p-4 bg-off-white border border-light-gray rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-charcoal flex items-center gap-2">
                    Item {index + 1}
                    {auto_populated && index === 0 && (
                      <span className="px-2 py-0.5 bg-lime-green text-forest-green text-xs rounded-full font-medium">
                        From Meter Reading
                      </span>
                    )}
                  </span>
                  {form_data.line_items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => remove_line_item(index)}
                      className="text-error hover:text-error hover:opacity-80 transition-all"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>

                <Input
                  label="Description"
                  placeholder="e.g., Water Consumption"
                  value={item.description}
                  onChange={(e) => handle_line_item_change(index, 'description', e.target.value)}
                  error={errors[`line_item_${index}_description`]}
                  is_required
                />

                <div className="grid grid-cols-3 gap-3">
                  <Input
                    label="Quantity (m³)"
                    type="number"
                    step="0.01"
                    placeholder="0"
                    value={item.quantity || ''}
                    onChange={(e) => handle_line_item_change(index, 'quantity', parseFloat(e.target.value) || 0)}
                    error={errors[`line_item_${index}_quantity`]}
                    is_required
                    disabled={auto_populated && index === 0}
                    helper_text={auto_populated && index === 0 ? 'Locked to meter reading' : undefined}
                  />

                  <Input
                    label="Unit Price (KES)"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={item.unit_price || ''}
                    onChange={(e) => handle_line_item_change(index, 'unit_price', parseFloat(e.target.value) || 0)}
                    error={errors[`line_item_${index}_unit_price`]}
                    is_required
                  />

                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-1.5">
                      Amount
                    </label>
                    <div className="px-4 py-2 bg-white border border-light-gray rounded-lg font-semibold text-forest-green">
                      {format_currency(item.amount)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tax & Totals */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-charcoal uppercase tracking-wide">
            Tax & Totals
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Tax Rate (%)"
              type="number"
              step="0.01"
              placeholder="16"
              value={form_data.tax_rate}
              onChange={(e) => handle_change('tax_rate', parseFloat(e.target.value) || 0)}
              icon_left={<Percent size={18} />}
              error={errors.tax_rate}
              helper_text="Default VAT rate is 16%"
            />

            <div className="p-4 bg-primary-50 rounded-lg border border-forest-green space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-medium-gray">Subtotal:</span>
                <span className="font-medium text-charcoal">{format_currency(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-medium-gray">Tax ({form_data.tax_rate}%):</span>
                <span className="font-medium text-charcoal">{format_currency(tax_amount)}</span>
              </div>
              <div className="flex justify-between text-lg pt-2 border-t border-forest-green">
                <span className="font-semibold text-charcoal">Total:</span>
                <span className="font-bold text-forest-green">{format_currency(total)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Notes & Terms */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-charcoal uppercase tracking-wide">
            Additional Information
          </h3>

          <div>
            <label className="block text-sm font-medium text-charcoal mb-1.5">
              Notes (Optional)
            </label>
            <textarea
              value={form_data.notes}
              onChange={(e) => handle_change('notes', e.target.value)}
              placeholder="Any additional notes or information..."
              rows={3}
              className="w-full px-4 py-3 border border-light-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-forest-green focus:border-forest-green transition-all resize-none text-charcoal"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-charcoal mb-1.5">
              Payment Terms
            </label>
            <textarea
              value={form_data.terms}
              onChange={(e) => handle_change('terms', e.target.value)}
              placeholder="Payment terms and conditions..."
              rows={2}
              className="w-full px-4 py-3 border border-light-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-forest-green focus:border-forest-green transition-all resize-none text-charcoal"
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};